'use client';

import {useEffect, useId, useRef, useState} from 'react';
import {cn} from '@/matic/lib/utils/cn';
import {MarkdownFieldText} from '@/matic/features/_shared/components/field-display/MarkdownFieldText';

type MarkdownTextFieldProps = {
  value: unknown;
  onChange: (value: unknown) => void;
  error?: string;
  disabled?: boolean;
  label: string;
  required?: boolean;
  maxLength?: number;
};

// Only surface the character count once the user is closing in on the cap —
// no need to clutter the field for anyone far from the limit.
const APPROACHING_LIMIT_THRESHOLD = 0.9;

/**
 * Long-text field editor for MDM records (Tribal Knowledge, description,
 * notes, and other LONG_TEXT_FIELD_KEYS) — a roomier textarea than the plain
 * `TextField multiline`, with a Write/Preview toggle so the markdown source
 * can be checked as rendered prose (same styling as Organization Knowledge)
 * without leaving the form.
 */
export function MarkdownTextField({
  value,
  onChange,
  error,
  disabled,
  label,
  required,
  maxLength,
}: MarkdownTextFieldProps) {
  const id = useId();
  const stringValue = typeof value === 'string' ? value : '';
  const [mode, setMode] = useState<'write' | 'preview'>('preview');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const shouldFocusEditorRef = useRef(false);
  const isEmpty = stringValue.trim().length === 0;
  const isApproachingLimit =
    maxLength !== undefined &&
    stringValue.length >= maxLength * APPROACHING_LIMIT_THRESHOLD;

  useEffect(() => {
    if (mode !== 'write' || !shouldFocusEditorRef.current) return;
    shouldFocusEditorRef.current = false;
    textareaRef.current?.focus();
  }, [mode]);

  const switchToWrite = () => {
    shouldFocusEditorRef.current = true;
    setMode('write');
  };

  return (
    <div>
      <div className="mb-1 flex items-center justify-between gap-3">
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500"> *</span>}
        </label>
        <div className="flex shrink-0 items-center rounded-md border border-neutral-200 p-0.5 text-xs">
          <button
            type="button"
            aria-pressed={mode === 'write'}
            onClick={disabled ? undefined : switchToWrite}
            className={cn(
              'rounded px-2 py-0.5 font-medium transition-colors',
              mode === 'write'
                ? 'bg-neutral-900 text-white'
                : 'text-neutral-500 hover:text-neutral-700'
            )}
          >
            Write
          </button>
          <button
            type="button"
            aria-pressed={mode === 'preview'}
            onClick={() => {
              setMode('preview');
            }}
            className={cn(
              'rounded px-2 py-0.5 font-medium transition-colors',
              mode === 'preview'
                ? 'bg-neutral-900 text-white'
                : 'text-neutral-500 hover:text-neutral-700'
            )}
          >
            Preview
          </button>
        </div>
      </div>

      {mode === 'write' ? (
        <textarea
          ref={textareaRef}
          id={id}
          value={stringValue}
          onChange={(e) => {
            onChange(e.target.value);
          }}
          disabled={disabled}
          rows={6}
          maxLength={maxLength}
          placeholder="Markdown supported — headings, lists, links, tables…"
          className={cn(
            'block w-full resize-y rounded-md border px-3 py-2 font-mono text-sm leading-relaxed shadow-sm focus:outline-none focus:ring-1 disabled:cursor-not-allowed disabled:bg-neutral-100 disabled:opacity-50',
            error
              ? 'border-error-500 focus:border-error-500 focus:ring-error-500'
              : 'border-neutral-300 focus:border-primary-500 focus:ring-primary-500'
          )}
        />
      ) : (
        <div
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={`Edit ${label}`}
          onClick={(event) => {
            if (disabled) return;
            if ((event.target as HTMLElement).closest('a')) return;
            switchToWrite();
          }}
          onKeyDown={(event) => {
            if (disabled) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              switchToWrite();
            }
          }}
          className={cn(
            'min-h-[152px] rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 transition-colors',
            disabled
              ? 'cursor-not-allowed opacity-60'
              : 'cursor-text hover:border-neutral-300 hover:bg-white focus:outline-none focus:ring-1 focus:ring-primary-500'
          )}
        >
          {isEmpty ? (
            <p className="text-sm italic text-neutral-400">
              {disabled
                ? 'Nothing to preview yet.'
                : 'Nothing to preview yet. Click to write.'}
            </p>
          ) : (
            <MarkdownFieldText value={stringValue} />
          )}
        </div>
      )}

      {error ? (
        <p className="mt-1 text-xs text-red-500">{error}</p>
      ) : (
        <p
          className={cn(
            'mt-1 text-xs',
            isApproachingLimit ? 'text-warning-600' : 'text-neutral-400'
          )}
        >
          {isApproachingLimit
            ? `${stringValue.length} / ${maxLength} characters`
            : 'Markdown supported'}
        </p>
      )}
    </div>
  );
}
